import React, { useState, useEffect, useCallback } from "react";
import { uploadMedia, listMedia, deleteMedia, getMediaUrl } from "@/lib/api";
import { Upload, Trash2, Copy, Check, Image, FileText, Film, X, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

export function MediaUploadButton({ onUpload, accept = "image/*,application/pdf,video/mp4" }) {
  const [uploading, setUploading] = useState(false);

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const res = await uploadMedia(file);
      onUpload?.(res.data);
    } catch (err) {
      alert("Erreur d'upload: " + (err.response?.data?.detail || err.message));
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  return (
    <label className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-[#1670B9] text-white text-sm font-medium cursor-pointer hover:bg-[#125c9e] transition-colors" data-testid="media-upload-btn">
      {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
      {uploading ? "Upload..." : "Uploader"}
      <input type="file" className="hidden" accept={accept} onChange={handleFile} disabled={uploading} />
    </label>
  );
}

function getFileIcon(ct) {
  if (ct?.startsWith("image")) return <Image className="w-5 h-5 text-[#1670B9]" />;
  if (ct?.startsWith("video")) return <Film className="w-5 h-5 text-[#F49C03]" />;
  return <FileText className="w-5 h-5 text-slate-400" />;
}

export function MediaPicker({ open, onClose, onSelect }) {
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);

  const refresh = useCallback(() => {
    setLoading(true);
    listMedia().then(r => setFiles(r.data)).catch(() => {}).finally(() => setLoading(false));
  }, []);

  useEffect(() => { if (open) refresh(); }, [open, refresh]);

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      await uploadMedia(file);
      refresh();
    } catch (err) {
      alert("Erreur: " + (err.response?.data?.detail || err.message));
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  const handleDelete = async (fileId) => {
    if (!window.confirm("Supprimer ce fichier ?")) return;
    await deleteMedia(fileId);
    refresh();
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-3xl max-h-[80vh] overflow-hidden flex flex-col" aria-describedby={undefined}>
        <DialogHeader>
          <DialogTitle className="font-['Quicksand'] flex items-center justify-between">
            <span>Gestionnaire de médias</span>
            <label className="inline-flex items-center gap-2 px-3 py-1.5 rounded-lg bg-[#1670B9] text-white text-xs font-medium cursor-pointer hover:bg-[#125c9e]">
              {uploading ? <Loader2 className="w-3 h-3 animate-spin" /> : <Upload className="w-3 h-3" />}
              Uploader
              <input type="file" className="hidden" accept="image/*,application/pdf,video/mp4" onChange={handleUpload} />
            </label>
          </DialogTitle>
        </DialogHeader>
        <div className="flex-1 overflow-y-auto mt-4">
          {loading ? (
            <div className="flex items-center justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-[#1670B9]" /></div>
          ) : files.length === 0 ? (
            <div className="text-center py-12 text-slate-400">
              <Image className="w-10 h-10 mx-auto mb-3 text-slate-300" />
              <p>Aucun fichier. Uploadez votre premier média.</p>
            </div>
          ) : (
            <div className="grid grid-cols-3 sm:grid-cols-4 gap-3">
              {files.map((f) => (
                <div key={f.file_id || f.id} className="group relative rounded-xl border border-slate-100 overflow-hidden bg-slate-50 hover:border-[#1670B9] transition-colors cursor-pointer"
                  onClick={() => { onSelect?.(getMediaUrl(f.file_id)); onClose?.(false); }}>
                  {f.content_type?.startsWith("image") ? (
                    <img src={getMediaUrl(f.file_id)} alt={f.original_filename} className="w-full h-24 object-cover" />
                  ) : (
                    <div className="w-full h-24 flex items-center justify-center">{getFileIcon(f.content_type)}</div>
                  )}
                  <div className="p-1.5">
                    <p className="text-[10px] text-slate-500 truncate">{f.original_filename}</p>
                  </div>
                  <button
                    onClick={(e) => { e.stopPropagation(); handleDelete(f.file_id); }}
                    className="absolute top-1 right-1 w-5 h-5 rounded bg-red-500 text-white opacity-0 group-hover:opacity-100 flex items-center justify-center transition-opacity"
                  >
                    <Trash2 className="w-3 h-3" />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default function MediaManager() {
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(null);

  const refresh = () => {
    setLoading(true);
    listMedia().then(r => setFiles(r.data)).catch(() => {}).finally(() => setLoading(false));
  };

  useEffect(() => { refresh(); }, []);

  const handleUpload = async (e) => {
    const fileList = e.target.files;
    if (!fileList?.length) return;
    for (const file of fileList) {
      try { await uploadMedia(file); } catch (err) { alert("Erreur: " + err.message); }
    }
    refresh();
    e.target.value = "";
  };

  const copyUrl = (fileId) => {
    navigator.clipboard.writeText(getMediaUrl(fileId));
    setCopied(fileId);
    setTimeout(() => setCopied(null), 2000);
  };

  return (
    <div data-testid="media-manager">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="font-['Quicksand'] text-xl font-semibold text-slate-800">Gestionnaire de médias</h2>
          <p className="text-sm text-slate-400 mt-1">{files.length} fichier(s)</p>
        </div>
        <label className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl bg-[#1670B9] text-white text-sm font-medium cursor-pointer hover:bg-[#125c9e] transition-colors">
          <Upload className="w-4 h-4" /> Uploader des fichiers
          <input type="file" multiple className="hidden" accept="image/*,application/pdf,video/mp4,.svg" onChange={handleUpload} />
        </label>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-[#1670B9]" /></div>
      ) : files.length === 0 ? (
        <div className="text-center py-20 bg-white rounded-xl border border-slate-100">
          <Upload className="w-12 h-12 mx-auto mb-4 text-slate-300" />
          <p className="text-slate-400">Aucun fichier uploadé. Glissez des fichiers ou cliquez sur Uploader.</p>
        </div>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
          {files.map((f) => {
            const fid = f.file_id || f.id;
            return (
              <div key={fid} className="group bg-white rounded-xl border border-slate-100 overflow-hidden hover:shadow-sm transition-shadow" data-testid={`media-item-${fid}`}>
                {f.content_type?.startsWith("image") ? (
                  <img src={getMediaUrl(fid)} alt={f.original_filename} className="w-full h-32 object-cover" loading="lazy" />
                ) : (
                  <div className="w-full h-32 flex items-center justify-center bg-slate-50">{getFileIcon(f.content_type)}</div>
                )}
                <div className="p-3">
                  <p className="text-xs text-slate-700 font-medium truncate">{f.original_filename}</p>
                  <p className="text-[10px] text-slate-400 mt-0.5">{f.ext?.toUpperCase()} &bull; {(f.size / 1024).toFixed(0)} KB</p>
                  <div className="flex items-center gap-1.5 mt-2">
                    <button onClick={() => copyUrl(fid)} className="flex-1 flex items-center justify-center gap-1 px-2 py-1 rounded-md bg-slate-50 text-xs text-slate-500 hover:bg-[#1670B9]/10 hover:text-[#1670B9] transition-colors">
                      {copied === fid ? <Check className="w-3 h-3" /> : <Copy className="w-3 h-3" />}
                      {copied === fid ? "Copié" : "URL"}
                    </button>
                    <button onClick={async () => { await deleteMedia(fid); refresh(); }} className="px-2 py-1 rounded-md bg-slate-50 text-xs text-slate-400 hover:bg-red-50 hover:text-red-500 transition-colors">
                      <Trash2 className="w-3 h-3" />
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
